import { IonItem, IonLabel, IonLoading, IonToggle } from "@ionic/react";
import React, { useCallback, useContext } from "react";
import { TranslationsContext } from "../util/Translations";
import { useOfflineStore } from "../store/offline-mode";
import { useHistory } from "react-router";
import { useWorkOrderStore } from "../store/wo-order";
import useSyncOfflineUpdates from "../hooks/offline/useSyncOfflineUpdates";
import { useUserStore } from "../store/user";
import { useSettingsStore } from "../store/settings";
import { useEquipmentStore } from "../store/equipment";
import { useCycleCountStore } from "../store/cycle-count";

const OfflineToggle: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const isOffline = useOfflineStore((state) => state.isOffline);
  const toggleOffline = useOfflineStore((state) => state.toggleOffline);
  const resetWoStore = useWorkOrderStore((state) => state.reset);
  const resetEquipmentStore = useEquipmentStore((state) => state.reset);
  const resetUserStore = useUserStore((state) => state.reset);
  const resetSettingStore = useSettingsStore((state) => state.reset);
  // reset only the cycle-count DATA store (downloaded batches/items/meta); the
  // write-back queue (cycle-count-offline-updates) is intentionally left intact
  // so failed submissions can still be retried — mirrors the WO reset behavior
  const resetCycleCountStore = useCycleCountStore((state) => state.reset);
  const history = useHistory();
  const { syncOfflineUpdates, isSyncing } = useSyncOfflineUpdates();

  const handleOfflineToggle = useCallback(async () => {
    toggleOffline();
    //when offline is toggled on
    if (!isOffline) history.push("/WorkOrders/true");
    //when offline is toggled off
    if (isOffline) {
      try {
        await syncOfflineUpdates(); //start syncing
      } finally {
        //reset storage and route to the upload-data page even if sync errored
        resetWoStore();
        resetEquipmentStore();
        resetUserStore();
        resetSettingStore();
        resetCycleCountStore();
        history.replace("/offline/upload-data");
      }
    }
  }, [
    history,
    isOffline,
    resetCycleCountStore,
    resetEquipmentStore,
    resetSettingStore,
    resetUserStore,
    resetWoStore,
    syncOfflineUpdates,
    toggleOffline,
  ]);

  return (
    <IonItem lines="none">
      <IonLoading
        isOpen={isSyncing}
        message={
          translations["lbl_mob_offline_syncing"] ||
          "Syncing your offline changes. This may take a while for large counts — please keep the app open."
        }
      />
      <IonLabel className="ion-text-wrap">
        <h2>{translations["lbl_mob_work_offline"] || "Work offline"}</h2>
        <p>
          {isOffline
            ? translations["lbl_mob_offline_helper_offline"] ||
              "You're offline. Work orders are loaded from this device."
            : translations["lbl_mob_offline_helper_online"] ||
              "You're online. Work orders are loaded from EAM."}
        </p>
      </IonLabel>
      <IonToggle
        slot="end"
        checked={isOffline}
        onClick={handleOfflineToggle}
        aria-label={translations["lbl_mob_work_offline"] || "Work offline"}
      />
    </IonItem>
  );
};

export default OfflineToggle;
